import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getDefaultRouteLabel } from '../config/routeLabels';

export interface NavEntry {
  path: string;
  label: string;
}

interface NavigationHistoryContextValue {
  /** Previous pages (up to 3), oldest first */
  history: NavEntry[];
  /** Current page */
  current: NavEntry | null;
  /** Override the current page's label (e.g., with an entity name) */
  setPageLabel: (label: string) => void;
}

const STORAGE_KEY = 'nav_history';

/** Pass as `state` on a top-menu Link: the destination starts a new trail
 *  instead of nesting under the pages the user came from. */
// eslint-disable-next-line react-refresh/only-export-components
export const NAV_RESET_STATE = { navReset: true } as const;
const MAX_HISTORY = 3; // previous pages to keep

const NavigationHistoryContext = createContext<NavigationHistoryContextValue | undefined>(undefined);

function loadFromStorage(): NavEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveToStorage(entries: NavEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}

export const NavigationHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { pathname } = location;
  const navReset = (location.state as { navReset?: boolean } | null)?.navReset === true;
  const { user } = useAuth();

  // Full trail: [...previous, current]. Loaded from sessionStorage on mount.
  const [trail, setTrail] = useState<NavEntry[]>(() => loadFromStorage());
  const prevPathnameRef = useRef<string | null>(null);
  const prevKeyRef = useRef<string | null>(null);

  // On logout, clear trail. Synchronizes with sessionStorage (an external
  // system), which is exactly what effects are for — justifies the disable.
  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTrail([]);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  // On pathname change, push new entry. Same rationale: this mirrors
  // react-router navigation (external system) into sessionStorage-backed trail state.
  useEffect(() => {
    if (!user) return;
    const isNewReset = navReset && location.key !== prevKeyRef.current;
    prevKeyRef.current = location.key;
    if (pathname === prevPathnameRef.current && !isNewReset) return;
    prevPathnameRef.current = pathname;

    const label = getDefaultRouteLabel(pathname) || pathname;

    if (isNewReset) {
      const fresh = [{ path: pathname, label }];
      saveToStorage(fresh);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTrail(fresh);
      return;
    }

    setTrail(prev => {
      // Skip if the last entry is the same path (dedup consecutive)
      if (prev.length > 0 && prev[prev.length - 1].path === pathname) {
        return prev;
      }

      const newTrail = [...prev, { path: pathname, label }];
      // Keep at most MAX_HISTORY previous + 1 current
      const trimmed = newTrail.slice(-(MAX_HISTORY + 1));
      saveToStorage(trimmed);
      return trimmed;
    });
  }, [pathname, location.key, navReset, user]);

  const setPageLabel = useCallback((label: string) => {
    setTrail(prev => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      updated[updated.length - 1] = { ...updated[updated.length - 1], label };
      saveToStorage(updated);
      return updated;
    });
  }, []);

  const history = trail.length > 1 ? trail.slice(0, -1) : [];
  const current = trail.length > 0 ? trail[trail.length - 1] : null;

  return (
    <NavigationHistoryContext.Provider value={{ history, current, setPageLabel }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
};

// Co-located with NavigationHistoryProvider by convention; splitting into a
// separate file would require updating import sites outside src/hooks and
// src/contexts, which is out of scope here.
// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationHistory() {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error('useNavigationHistory must be used within NavigationHistoryProvider');
  }
  return context;
}
